import { type AvatarSize } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useWorkplacePhotos } from "@src/appV2/Facilities/api/useWorkplacePhotos";
import { minutesToMilliseconds } from "date-fns";

import { WorkplaceAvatar } from "./WorkplaceAvatar";

interface WorkplaceAvatarWithPhotoProps {
  workplaceId: string;
  size: AvatarSize;
  index: number;
  totalCount: number;
}

function WorkplaceAvatarWithPhoto(props: WorkplaceAvatarWithPhotoProps) {
  const { workplaceId, size, index, totalCount } = props;

  const { data: photos, isLoading } = useWorkplacePhotos(
    { workplaceId },
    {
      enabled: isDefined(workplaceId),
      staleTime: minutesToMilliseconds(5),
    }
  );

  const firstPhoto = photos?.photosList?.[0];

  return (
    <Stack
      sx={(theme) => ({
        // Create overlap by applying negative margin to all except the last avatar
        marginRight: index === totalCount - 1 ? 0 : -5,
        // Ensure higher z-index for later avatars so they appear on top
        zIndex: index,
        // Add white border around the avatar container
        "& > *": {
          border: `${theme.borderWidth?.regular} solid ${theme.palette.background.tertiary}`,
          borderRadius: theme.borderRadius?.medium,
        },
      })}
    >
      <WorkplaceAvatar size={size} pictureUrl={firstPhoto?.url} isLoading={isLoading} />
    </Stack>
  );
}

interface OverlappingWorkplaceAvatarsProps {
  workplaceIds: string[];
  size?: AvatarSize;
  maxVisibleAvatars?: number;
}

export function OverlappingWorkplaceAvatars(props: OverlappingWorkplaceAvatarsProps) {
  const { workplaceIds, size = "small", maxVisibleAvatars = 3 } = props;

  const visibleWorkplaces = workplaceIds.slice(0, maxVisibleAvatars);

  return (
    <Stack direction="row">
      {visibleWorkplaces.map((workplaceId, index) => (
        <WorkplaceAvatarWithPhoto
          key={workplaceId}
          workplaceId={workplaceId}
          size={size}
          index={index}
          totalCount={visibleWorkplaces.length}
        />
      ))}
    </Stack>
  );
}
